import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ArrowLeft, UserCheck, UserX, Shield, User as UserIcon, Clock, CheckCircle, XCircle, Eye, Pencil, Ban, EyeOff, Server } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

export const UsersAdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get('/users'),
        api.get('/roles')
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.put(`/users/${userId}`, { approved: true });
      toast.success('Uživatel schválen');
      fetchData();
    } catch (error) {
      toast.error('Chyba při schvalování uživatele');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await api.put(`/users/${userId}`, { approved: false });
      toast.success('Schválení zrušeno');
      fetchData();
    } catch (error) {
      toast.error('Chyba při zrušení schválení');
    }
  };

  const handleRoleChange = async (userId, roleId) => {
    try {
      await api.put(`/users/${userId}`, { role_id: roleId });
      toast.success('Role změněna');
      fetchData();
    } catch (error) {
      toast.error('Chyba při změně role');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('Uživatel smazán');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při mazání uživatele');
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'pending') return !user.approved;
    if (filter === 'approved') return user.approved;
    return true;
  });

  const pendingCount = users.filter(u => !u.approved).length;

  // Check if user is admin (legacy)
  const isLegacyAdmin = (user) => {
    return user.role === 'admin' || (user.permissions && user.permissions.includes('admin'));
  };

  // Get permission summary badges
  const getPermissionBadges = (user) => {
    if (isLegacyAdmin(user)) {
      return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Administrátor</Badge>;
    }
    
    const perms = user.page_permissions || {};
    const editCount = Object.values(perms).filter(v => v === 'edit').length;
    const viewCount = Object.values(perms).filter(v => v === 'view').length;
    const deniedCount = Object.values(perms).filter(v => v === 'denied').length;
    
    return (
      <div className="flex flex-wrap gap-1">
        {editCount > 0 && (
          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
            <Pencil className="h-3 w-3 mr-1" />
            {editCount}
          </Badge>
        )}
        {viewCount > 0 && (
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
            <Eye className="h-3 w-3 mr-1" />
            {viewCount}
          </Badge>
        )}
        {deniedCount > 0 && (
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
            <Ban className="h-3 w-3 mr-1" />
            {deniedCount}
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="users-admin-page">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Správa uživatelů</h1>
            <p className="text-lg text-slate-600">Schvalování registrací a přiřazení rolí</p>
          </div>
          <div className="flex gap-3">
            {pendingCount > 0 && (
              <Badge className="bg-amber-500 text-white text-lg px-4 py-2">
                <Clock className="h-4 w-4 mr-2" />
                {pendingCount} čeká na schválení
              </Badge>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/admin/roles')}
              className="border-cyan-300"
              data-testid="manage-roles-button"
            >
              <Shield className="h-4 w-4 mr-2" />
              Spravovat role
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/system-log')}
              className="border-slate-300"
              data-testid="system-log-button"
            >
              <Server className="h-4 w-4 mr-2" />
              Systémové logy
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl border border-cyan-200 p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-700">Filtr:</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-64" data-testid="user-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všichni uživatelé ({users.length})</SelectItem>
                <SelectItem value="pending">Čekající na schválení ({pendingCount})</SelectItem>
                <SelectItem value="approved">Schválení ({users.length - pendingCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-cyan-200 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-cyan-50 border-b border-cyan-200">
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Uživatel
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Role
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Oprávnění
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Stav
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Registrace
                </th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-cyan-100 transition-colors hover:bg-cyan-50/30"
                  data-testid={`user-row-${user.id}`}
                >
                  <td className="p-4 align-middle">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white flex items-center justify-center font-semibold">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{user.name}</span>
                          {isLegacyAdmin(user) && (
                            <Shield className="h-4 w-4 text-purple-500" />
                          )}
                        </div>
                        <span className="text-sm text-slate-500">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 align-middle">
                    {isLegacyAdmin(user) ? (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                        <Shield className="h-3 w-3 mr-1" />
                        Administrátor
                      </Badge>
                    ) : (
                      <Select
                        value={user.role_id || 'role_viewer'}
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                      >
                        <SelectTrigger 
                          className="w-48 h-9 text-sm"
                          data-testid={`role-select-${user.id}`}
                        >
                          <SelectValue placeholder="Vyberte roli" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              <div className="flex items-center gap-2">
                                {role.is_admin && <Shield className="h-3 w-3 text-purple-500" />}
                                {role.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="p-4 align-middle">
                    {getPermissionBadges(user)}
                  </td>
                  <td className="p-4 align-middle">
                    {user.approved ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Schváleno
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Čeká
                      </Badge>
                    )}
                  </td>
                  <td className="p-4 align-middle text-sm text-slate-700">
                    {format(new Date(user.created_at), 'd. M. yyyy', { locale: cs })}
                  </td>
                  <td className="p-4 align-middle">
                    <div className="flex gap-2">
                      {!user.approved ? (
                        <Button
                          size="sm"
                          onClick={() => handleApproveUser(user.id)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          data-testid={`approve-user-${user.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Schválit
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectUser(user.id)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`reject-user-${user.id}`}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Zrušit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDeleteDialog(user)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <UserIcon className="h-12 w-12 mx-auto mb-3" />
              <p>Žádní uživatelé k zobrazení</p>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent data-testid="delete-user-dialog">
            <DialogHeader>
              <DialogTitle>Smazat uživatele</DialogTitle>
              <DialogDescription>
                Opravdu chcete smazat uživatele <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                Tato akce je nevratná.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                data-testid="cancel-delete-button"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-button"
              >
                Smazat uživatele
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
